import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { SiteView } from '~/components/site/SiteView';
import { PopupDialog } from '~/components/ui/PopupDialog';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useContacts } from '~/routes/resources/contacts';
import { formatLocalDate } from '../../formatDate';
import { Job } from '../JobView';
import { WhatBubble, WhenBubble, WhereBubble, WhoBubble } from './Bubbles';

type Props = {
  job: Job;
};

// Card component defined OUTSIDE JobReadView to prevent remounting children on every render
// When defined inside, React creates a new component type each render, causing child remounts
type CardProps = {
  label: string;
  children: ReactNode;
  isMobile: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const Card = ({ label, children, isMobile, ...rest }: CardProps) => (
  <div
    className={classNames(
      'flex flex-col gap-5 rounded-lg border border-outline-variant bg-surface-container-background shadow-md',
      isMobile ? 'px-[13px] pt-4' : 'px-5 pt-5'
    )}
    {...rest}
  >
    <h6 className='text-lg font-medium leading-5'>{label}</h6>
    <div className={classNames(isMobile ? 'pb-4' : 'pb-5')}>{children}</div>
  </div>
);

export function JobReadView({ job }: Props) {
  const contacts = useContacts();
  const { isMobile } = useBreakpoint();
  const [showSite, setShowSite] = useState(false);

  const jobStart = formatLocalDate(job, 'scheduledStart');
  const jobEnd = formatLocalDate(job, 'scheduledEnd');
  const site = job.location?.__typename === 'Site' ? job.location : undefined;

  return (
    <div
      className={classNames('flex flex-col', isMobile ? 'gap-[13px]' : 'gap-5')}
    >
      {job.location?.name && (
        <Card data-usetiful='where' label='Where' isMobile={isMobile}>
          <WhereBubble job={job} onPlaceNameClick={() => setShowSite(true)} />
        </Card>
      )}

      {/* {whenSummary(job) !== '' && ( */}
      {(jobStart || jobEnd) && (
        <Card data-usetiful='when' label='When' isMobile={isMobile}>
          <WhenBubble job={job} />
        </Card>
      )}

      {job.assignees?.length && (
        <Card data-usetiful='who' label='Who' isMobile={isMobile}>
          <WhoBubble job={job} contacts={contacts} />
        </Card>
      )}

      <Card data-usetiful='what' label='What' isMobile={isMobile}>
        <WhatBubble job={job} contacts={contacts} />
      </Card>

      <PopupDialog isOpen={showSite} onClose={() => setShowSite(false)}>
        <PopupDialog.Title>{site?.name}</PopupDialog.Title>
        {job.location && (
          <SiteView
            isPopup={true}
            siteId={job.location.id}
            onClose={() => setShowSite(false)}
          />
        )}
      </PopupDialog>
    </div>
  );
}
